import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../css/AddUnit.css";

const AddRoomBooking = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const API_URL = process.env.REACT_APP_API_URL || "";

  const isEditMode = Boolean(id);

  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);

  const [roomId, setRoomId] = useState("");
  const [bedId, setBedId] = useState("");
  const [checkingDate, setCheckingDate] = useState("");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [address, setAddress] = useState("");
  const [idProofType, setIdProofType] = useState("Aadhaar");
  const [idProofImage, setIdProofImage] = useState(null); // File input, can't prefill
  const [perDayPrice, setPerDayPrice] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [nights, setNights] = useState(0);

  const [customer_details, setCustomerDetails] = useState("");
  const [customer_getepass, setCustomerGetepass] = useState(null); // File input, can't prefill

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [currentIdProofImage, setCurrentIdProofImage] = useState("");
  const [currentGetepassImage, setCurrentGetepassImage] = useState("");

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 5000);
  };

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await fetch(`${API_URL}room-list.php`);
        const data = await response.json();
        if (data.status === "success") {
          setRooms(data.data);
          // Set roomId again after rooms are loaded (edit mode)
          if (isEditMode && roomId) {
            setRoomId(String(roomId));
          }
        }
      } catch (error) {
        showToast("Could not fetch rooms", "error");
      }
    };
    fetchRooms();
    // eslint-disable-next-line
  }, [API_URL, isEditMode, roomId]);

  useEffect(() => {
    if (!roomId) {
      setBeds([]);
      setBedId("");
      return;
    }
    const fetchBeds = async () => {
      try {
        const response = await fetch(
          `${API_URL}room-no-wise-bed.php?room_no_id=${roomId}`
        );
        const data = await response.json();
        setBeds(data.status === "success" ? data.data : []);
        // Set bedId again after beds are loaded (edit mode)
        if (isEditMode && bedId) {
          setBedId(String(bedId));
        }
      } catch (error) {
        showToast("Could not fetch beds for the selected room", "error");
      }
    };
    fetchBeds();
    // eslint-disable-next-line
  }, [roomId, API_URL, isEditMode, bedId]);

  useEffect(() => {
    if (!bedId) {
      setPerDayPrice("");
      return;
    }
    const fetchPrice = async () => {
      try {
        const response = await fetch(
          `${API_URL}get-per-day-price.php?bed_id=${bedId}`
        );
        const data = await response.json();
        if (data.status === "success" && data.data.length > 0) {
          setPerDayPrice(data.data[0].per_day_price);
        }
      } catch (error) {
        showToast("Could not fetch price for the selected bed", "error");
      }
    };
    fetchPrice();
  }, [bedId, API_URL]);

  useEffect(() => {
    if (checkingDate && checkoutDate && perDayPrice > 0) {
      const checkIn = new Date(checkingDate);
      const checkOut = new Date(checkoutDate);

      if (checkOut <= checkIn) {
        setNights(1);
        setTotalAmount(parseFloat(perDayPrice));
        return;
      }

      const differenceInTime = checkOut.getTime() - checkIn.getTime();
      const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

      const calculatedNights = differenceInDays === 0 ? 1 : differenceInDays;
      setNights(calculatedNights);
      setTotalAmount(calculatedNights * parseFloat(perDayPrice));
    }
  }, [checkingDate, checkoutDate, perDayPrice]);

  useEffect(() => {
    if (isEditMode && id) {
      fetch(`${API_URL}get-bed-booking.php?id=${id}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.data) {
            const booking = data.data;
            setRoomId(String(booking.room_no_id));
            setBedId(String(booking.bed_no_id));
            setCheckingDate(booking.checking_date);
            setCheckoutDate(booking.checkout_date);
            setCustomerName(booking.customer_name);
            setAddress(booking.address);
            setIdProofType(booking.id_proof_type);
            setPerDayPrice(booking.per_day_price);
            setTotalAmount(booking.total_amount);
            setNights(booking.nodays);
            setCustomerDetails(booking.customer_details);
            setCurrentIdProofImage(booking.id_proof_image || "");
            setCurrentGetepassImage(booking.customer_getepass || "");
            // File inputs cannot be prefilled
          }
        });
    }
    // eslint-disable-next-line
  }, [isEditMode, id, API_URL]);

  useEffect(() => {
    if (!isEditMode) {
      setRoomId("");
      setBedId("");
      setCheckingDate("");
      setCheckoutDate("");
      setCustomerName("");
      setAddress("");
      setIdProofType("Aadhaar");
      setIdProofImage(null);
      setPerDayPrice("");
      setTotalAmount(0);
      setNights(0);
      setCustomerDetails("");
      setCustomerGetepass(null);
      setCurrentIdProofImage("");
      setCurrentGetepassImage("");
    }
    // eslint-disable-next-line
  }, [isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditMode && !idProofImage) {
      showToast("Please upload an ID proof image.", "error");
      return;
    }
    if (totalAmount <= 0) {
      showToast("Please check dates and price.", "error");
      return;
    }
    setIsSubmitting(true);

    if (isEditMode) {
      const formData = new FormData();
      formData.append("booking_id", id); // Send the booking ID for update
      formData.append("room_no_id", roomId);
      formData.append("bed_no_id", bedId);
      formData.append("checking_date", checkingDate);
      formData.append("checkout_date", checkoutDate);
      formData.append("customer_name", customerName);
      formData.append("address", address);
      formData.append("id_proof_type", idProofType);
      if (idProofImage) formData.append("id_proof_image", idProofImage);
      formData.append("per_day_price", perDayPrice);
      formData.append("total_amount", totalAmount);
      formData.append("nights", nights);
      formData.append("customer_details", customer_details);
      if (customer_getepass) formData.append("customer_getepass", customer_getepass);

      try {
        const response = await fetch(`${API_URL}bed-booking-update.php`, {
          method: "POST",
          body: formData,
        });
        const data = await response.json();
        if (data.success === true) {
          showToast("Booking updated successfully!", "success");
          setTimeout(() => navigate("/room-booking-list"), 1500);
        } else {
          showToast(data.message || "Update failed.", "error");
        }
      } catch (error) {
        showToast("Network error. Could not update booking.", "error");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const formData = new FormData();
    formData.append("room_no_id", roomId);
    formData.append("bed_no_id", bedId);
    formData.append("checking_date", checkingDate);
    formData.append("checkout_date", checkoutDate);
    formData.append("customer_name", customerName);
    formData.append("address", address);
    formData.append("id_proof_type", idProofType);
    formData.append("id_proof_image", idProofImage);
    formData.append("per_day_price", perDayPrice);
    formData.append("total_amount", totalAmount);
    formData.append("nights", nights);
    formData.append("customer_details",customer_details);
    formData.append("customer_getepass", customer_getepass);


    try {
      const response = await fetch(`${API_URL}add-bed-booking.php`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (data.success === true) {
        const successMessage = `Booking successful! Total: ₹${data.total_amount} for ${data.nights} night(s).`;
        showToast(successMessage, "success");
        setTimeout(() => navigate("/room-booking-list"), 2000);
      } else {
        showToast(data.message || "Booking failed.", "error");
      }
    } catch (error) {
      showToast("Network error. Could not submit booking.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{ display: "block" }}>
        <h1>{isEditMode ? "Edit Room Booking" : "Book a Room"}</h1>
        <p>{isEditMode ? "Update the booking details below." : "Fill in the customer details to book a bed in a room"}</p>
      </div>
      <div className="">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px 30px",
            }}
          >
            <div className="form-group">
              <label htmlFor="roomId">Room Number</label>
              <select
                id="roomId"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                required
                disabled={isSubmitting}
              >
                <option value="">Select Room</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.room_no}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="bedId">Bed Number</label>
              <select
                id="bedId"
                value={bedId}
                onChange={(e) => setBedId(e.target.value)}
                required
                disabled={!roomId || isSubmitting}
              >
                <option value="">Select Bed</option>
                {beds.map((bed) => (
                  <option key={bed.id} value={bed.id}>
                    {bed.bed_no}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="customerName">Customer Name</label>
            <input
              type="text"
              id="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              disabled={isSubmitting}
            ></textarea>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px 30px",
            }}
          >
            <div className="form-group">
              <label htmlFor="checkingDate">Check-in Date</label>
              <input
                type="date"
                id="checkingDate"
                value={checkingDate}
                onChange={(e) => setCheckingDate(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="checkoutDate">Check-out Date</label>
              <input
                type="date"
                id="checkoutDate"
                value={checkoutDate}
                onChange={(e) => setCheckoutDate(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="perDayPrice">Price Per Day (₹)</label>
              <input
                type="number"
                id="perDayPrice"
                value={perDayPrice}
                required
                readOnly
                placeholder="Select bed to see price"
              />
            </div>
            <div className="form-group">
              <label>Total Amount (₹)</label>
              <input
                type="number"
                value={totalAmount}
                disabled
                readOnly
                style={{ fontWeight: "bold" }}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px 30px",
            }}
          >
            <div className="form-group">
              <label htmlFor="idProofType">ID Proof Type</label>
              <select
                id="idProofType"
                value={idProofType}
                onChange={(e) => setIdProofType(e.target.value)}
                required
                disabled={isSubmitting}
              >
                <option value="Aadhaar">Aadhaar</option>
                <option value="Voter ID">Voter ID</option>
                <option value="Passport">Passport</option>
                <option value="Driving License">Driving License</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="idProofImage">ID Proof Image</label>
              <input
                type="file"
                id="idProofImage"
                onChange={(e) => setIdProofImage(e.target.files[0])}
                required={!isEditMode}
                disabled={isSubmitting}
                accept="image/*"
              />
              {isEditMode && currentIdProofImage && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={`${API_URL}uploads/${currentIdProofImage}`}
                    alt="Current ID Proof"
                    style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4, border: "1px solid #ccc" }}
                  />
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="customer_details">Customer Details</label>
              <textarea
                id="customer_details"
                value={customer_details}
                onChange={(e) => setCustomerDetails(e.target.value)}
              ></textarea>
            </div>
            <div className="form-group">
              <label htmlFor="customer_getepass">Customer Getepass</label>
              <input
                type="file"
                id="customer_getepass"
                onChange={(e) => setCustomerGetepass(e.target.files[0])}
                disabled={isSubmitting}
                accept="image/*"
              />
              {isEditMode && currentGetepassImage && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={`${API_URL}uploads/${currentGetepassImage}`}
                    alt="Current E-Pass"
                    style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4, border: "1px solid #ccc" }}
                  />
                </div>
              )}
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? (isEditMode ? "Updating..." : "Booking...") : isEditMode ? `Update Booking ₹${totalAmount}` : `Book for ₹${totalAmount}`}
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              {" "}
              ×{" "}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddRoomBooking;
